import { useQueryClient, UseQueryOptions } from 'react-query';
import { queryKey } from 'constants/queryKey';
import PostApi from 'api/post';
import { PostStatus } from '@kontentino/kontentino-constants/Posts';
import dayjs from 'dayjs';
import DateUtils from 'app/utils/date';
import useQueryNoCache from 'utils/hooks/useQueryNoCache';
import {
  useInvalidatePostDetailQueries,
  useInvalidatePostsQueries,
} from 'app/modules/posts/hooks/useInvalidatePostsQueries';
import { PostDetail } from 'types/PostDetail';

export function usePostOverviewQuery(
  id: number,
  options?: UseQueryOptions<PostDetail>,
) {
  return useQueryNoCache<PostDetail>(
    queryKey.postDetail(id),
    () => PostApi.detail(id),
    options,
  );
}

export function usePostOverviewOptimisticQueryUpdater(postId: number) {
  const queryClient = useQueryClient();
  const invalidatePostDetails = useInvalidatePostDetailQueries();
  const invalidatePosts = useInvalidatePostsQueries();

  return {
    update: (
      transform: (p: undefined | PostDetail) => undefined | Partial<PostDetail>,
    ) => {
      const prevPost = queryClient.getQueryData<PostDetail>(
        queryKey.postDetail(postId),
      );

      if (!prevPost) return;

      const transformedPostPreviewPartial = transform(prevPost);

      if (!transformedPostPreviewPartial) return;

      queryClient.setQueryData(queryKey.postDetail(postId), {
        ...prevPost,
        ...transformedPostPreviewPartial,
      });
    },
    invalidateData: (payload?: { commentThread?: string }) => {
      invalidatePostDetails({
        postId,
        commentThread: payload?.commentThread,
      });
      invalidatePosts();
    },
    transformers: {
      assignedTaskToggle(
        taskId: number,
        userId: number,
        prevPost?: PostDetail,
      ) {
        const assignedUser = prevPost?.assigned_users.find(
          (t) => t.task_id === taskId && t.user_id === userId,
        );

        if (assignedUser && prevPost?.assigned_users) {
          return {
            assigned_users: prevPost.assigned_users.map((t) =>
              t.task_id === taskId && t.user_id === userId
                ? {
                    ...t,
                    completed_at: !!t.completed_at
                      ? null
                      : DateUtils.toDateString(dayjs.utc()),
                  }
                : t,
            ),
          };
        }
      },
      requirementToggle(requirementId: number, prevPost?: PostDetail) {
        const requirement = prevPost?.requirements.find(
          (req) => req.id === requirementId,
        );

        if (requirement && prevPost?.requirements) {
          return {
            requirements: prevPost.requirements.map((req) =>
              req.id === requirement.id
                ? { ...requirement, checked: !requirement.checked }
                : req,
            ),
          };
        }
      },
      postStatus(status: PostStatus) {
        return { status };
      },
    },
  };
}
